// Content + SEO config for dedicated service landing pages.
// Each entry powers /services/:slug via ServiceLandingPage.js.
import {
  HeartPulse, Home, Stethoscope, Heart, Brain, Users, Activity,
} from 'lucide-react';

export const SERVICES = {
  'private-duty-nursing': {
    slug: 'private-duty-nursing',
    name: 'Private Duty Nursing',
    icon: HeartPulse,
    eyebrow: 'Skilled, one-on-one care',
    h1: 'Private Duty Nursing at Home',
    metaTitle: 'Private Duty Nursing at Home | CaraLoom',
    metaDescription:
      'Vetted RNs and LPNs deliver one-on-one private-duty nursing at home nationwide \u2014 orchestrated end-to-end on the CaraLoom OS for the agencies, hospitals, and independent practices behind every visit.',
    keywords: [
      'private duty nursing', 'in-home private nurse', 'private duty nurse near me',
      'RN home care', 'private nursing care at home',
    ],
    heroImage: 'https://images.pexels.com/photos/7659564/pexels-photo-7659564.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=900',
    heroAlt: 'Private duty nurse caring for an elderly patient at home',
    intro: [
      'Private duty nursing puts a licensed nurse at your loved one\u2019s side for the hours that matter most \u2014 not a rotating cast of strangers, but a consistent clinician who learns your family\u2019s routine, preferences, and medical history. CaraLoom connects families across the United States directly with vetted registered nurses (RNs) and licensed practical nurses (LPNs) who deliver skilled, one-on-one care in the comfort of home.',
      'Unlike a hand-off where every fax, schedule change, and bill rides on email, CaraLoom is the AI operating system underneath the entire episode. Families see each clinician\u2019s real credentials, experience, and reviews up front, and book through the agency or hospital running that service line on CaraLoom \u2014 often within hours of intake. Every nurse on the platform is identity-verified, license-checked, and background-screened before they can accept a single visit, and credentialing is continuously monitored.',
      'Whether you need a few hours of skilled oversight each day or around-the-clock coverage after a hospital discharge, private duty nursing gives families peace of mind that a qualified professional is monitoring medications, vital signs, and any change in condition \u2014 and escalating quickly if something looks off.',
    ],
    included: {
      title: 'What private duty nursing includes',
      items: [
        'Medication administration, management, and reconciliation',
        'Vital signs monitoring and clinical assessment',
        'Wound care, dressing changes, and post-surgical care',
        'IV therapy, injections, and infusion support',
        'Catheter, ostomy, and feeding-tube care',
        'Chronic disease management (diabetes, CHF, COPD)',
        'Care coordination with physicians and family',
        'Detailed, AI-assisted clinical notes after every visit',
      ],
    },
    whoFor: {
      title: 'Who private duty nursing is for',
      items: [
        'Seniors who want to age safely at home instead of a facility',
        'Patients recovering from surgery or a hospital stay',
        'Adults managing complex or multiple chronic conditions',
        'Families needing skilled overnight or 24-hour coverage',
        'Medically fragile children and young adults',
      ],
    },
    benefits: [
      { title: 'One consistent nurse', desc: 'Build a real relationship with a clinician who knows your loved one \u2014 not a different face every shift.' },
      { title: 'One coordinated record', desc: 'Notes, schedules, billing, and family updates live on one HIPAA-ready dashboard \u2014 no faxes, no spreadsheets.' },
      { title: 'Verified & insured', desc: 'Every RN and LPN is license-checked, background-screened, and continuously monitored.' },
      { title: 'Matched in hours', desc: 'Our AI surfaces nurses who fit your clinical needs, schedule, and location \u2014 fast.' },
    ],
    faqs: [
      { q: 'What is the difference between private duty nursing and home health?', a: 'Private duty nursing provides extended, one-on-one skilled care for as many hours as you need \u2014 often paid privately \u2014 while traditional home health is typically short, intermittent, physician-ordered visits covered by Medicare. CaraLoom is the operating system the agency or independent practice running your case uses to coordinate either model.' },
      { q: 'Do you provide RNs or LPNs?', a: 'Both. You can filter and choose the license level that matches the clinical complexity of your situation. RNs handle higher-acuity needs and assessments; LPNs are well-suited for ongoing skilled tasks under a care plan.' },
      { q: 'How quickly can a private duty nurse start?', a: 'Many families are matched with a vetted nurse within hours and begin care within a day or two, depending on your schedule and the clinician\u2019s availability.' },
      { q: 'Is private duty nursing covered by insurance?', a: 'Private duty nursing is most often paid privately or through long-term care insurance. Some plans and waivers may contribute. Clinicians on CaraLoom can declare which payers they accept, and we always recommend confirming coverage with your plan.' },
      { q: 'Can I get 24-hour or overnight nursing care?', a: 'Yes. You can arrange day shifts, overnight coverage, or around-the-clock care by booking one or more nurses to cover the hours you need.' },
      { q: 'How are nurses vetted on CaraLoom?', a: 'Every clinician completes identity verification, license validation, and background screening before accepting visits, and their license and insurance status is continuously monitored.' },
    ],
    related: ['skilled-nursing', 'post-surgery-care', 'home-health-aide'],
  },

  'skilled-nursing': {
    slug: 'skilled-nursing',
    name: 'Skilled Nursing Care',
    icon: Stethoscope,
    eyebrow: 'Clinical care, delivered home',
    h1: 'Skilled Nursing Care at Home',
    metaTitle: 'Skilled Nursing Care at Home | CaraLoom',
    metaDescription:
      'Connect with licensed RNs and NPs for skilled nursing at home nationwide \u2014 wound care, IV therapy, chronic care.',
    keywords: [
      'skilled nursing at home', 'home skilled nursing', 'in-home RN care',
      'wound care at home', 'IV therapy at home',
    ],
    heroImage: 'https://images.pexels.com/photos/4173251/pexels-photo-4173251.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=900',
    heroAlt: 'Skilled nurse checking a patient at home',
    intro: [
      'Skilled nursing care brings hospital-grade clinical expertise into the home. When a patient needs wound care, IV therapy, medication management, or close monitoring of a complex condition, a licensed nurse can deliver that care safely at home \u2014 reducing costly hospital readmissions and letting families recover where they\u2019re most comfortable.',
      'CaraLoom is the AI operating system the home-health agency, hospital-at-home program, or independent practice behind your case uses to orchestrate skilled care \u2014 referrals, scheduling, credentialing, and revenue cycle on a single HIPAA-ready stack. You see verified registered nurses (RNs) and nurse practitioners (NPs) across the United States with their real specialties and experience, and care begins quickly because every step from intake to documentation runs on the same system.',
      'Every skilled-nursing visit is documented with AI-assisted clinical notes, so your physician and family circle always have an accurate, up-to-date picture of progress. If a nurse notices a concerning change, they can coordinate quickly with the care team and escalate before a small issue becomes an emergency.',
    ],
    included: {
      title: 'What skilled nursing includes',
      items: [
        'Complex wound care and dressing changes',
        'IV therapy, infusions, and injections',
        'Vital signs and clinical assessments',
        'Chronic disease management and education',
        'Post-surgical and post-hospitalization care',
        'Catheter and ostomy management',
        'Medication management and reconciliation',
        'Care plan oversight and physician coordination',
      ],
    },
    whoFor: {
      title: 'Who skilled nursing is for',
      items: [
        'Patients recently discharged from a hospital or rehab',
        'Adults with wounds, infusions, or ongoing clinical needs',
        'People managing diabetes, heart failure, or COPD',
        'Families avoiding unnecessary facility stays',
        'Anyone needing professional clinical oversight at home',
      ],
    },
    benefits: [
      { title: 'Hospital-grade care at home', desc: 'Licensed RNs and NPs deliver complex clinical care where you\u2019re most comfortable.' },
      { title: 'Fewer readmissions', desc: 'Close monitoring and fast escalation help prevent avoidable trips back to the hospital.' },
      { title: 'AI-assisted notes', desc: 'Every visit is documented clearly and shared with your physician and family circle.' },
      { title: 'Transparent record', desc: 'Notes, schedules, and bills shared in one place with you and your physician \u2014 nothing hidden.' },
    ],
    faqs: [
      { q: 'What qualifies as skilled nursing care?', a: 'Skilled nursing is care that must be performed by a licensed nurse \u2014 such as wound care, IV therapy, injections, catheter care, and clinical assessments. It differs from personal care (bathing, dressing), which a home health aide can provide.' },
      { q: 'Can skilled nursing prevent hospital readmission?', a: 'Yes. Consistent in-home monitoring, medication management, and rapid escalation are proven to reduce avoidable readmissions, especially in the weeks after a discharge.' },
      { q: 'Do you offer wound care and IV therapy at home?', a: 'Absolutely. Many CaraLoom nurses specialize in advanced wound care, infusions, and IV therapy. You can filter by specialty to find the right match.' },
      { q: 'Will my doctor be kept in the loop?', a: 'Yes. Each visit generates clear clinical notes, and nurses coordinate with your physician and family so everyone stays aligned on the care plan.' },
      { q: 'How are clinicians verified?', a: 'All nurses are identity-verified, license-validated, and background-checked before accepting visits, with continuous monitoring of license and insurance status.' },
      { q: 'How soon can skilled nursing begin?', a: 'Often within a day or two. Our AI matching surfaces available, qualified nurses near you so care can start quickly after a discharge or new diagnosis.' },
    ],
    related: ['private-duty-nursing', 'post-surgery-care', 'home-health-aide'],
  },

  'home-health-aide': {
    slug: 'home-health-aide',
    name: 'Home Health Aides & Caregivers',
    icon: Home,
    eyebrow: 'Daily living support',
    h1: 'Home Health Aides & Personal Caregivers',
    metaTitle: 'Home Health Aides & Caregivers | CaraLoom',
    metaDescription:
      'Find vetted home health aides and personal caregivers for bathing, mobility, and companionship nationwide \u2014 coordinated by the agency running your case on the CaraLoom OS.',
    keywords: [
      'home health aide', 'personal care aide', 'caregiver near me',
      'home care aide', 'in-home caregiver',
    ],
    heroImage: 'https://images.pexels.com/photos/7551759/pexels-photo-7551759.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=900',
    heroAlt: 'Caregiver helping an elderly person at home',
    intro: [
      'A great home health aide is the difference between just getting by and truly thriving at home. CaraLoom connects families across the United States with vetted, compassionate aides and personal caregivers who help with the everyday tasks \u2014 bathing, dressing, mobility, meals, and companionship \u2014 that keep loved ones safe, dignified, and independent.',
      'CaraLoom is the operating system the agency or independent caregiver behind your case runs on. You see real caregiver profiles \u2014 experience, languages spoken, reviews \u2014 and pair with a consistent professional instead of a rotating cast of strangers, because their entire schedule, notes, and billing live on one HIPAA-ready dashboard.',
      'Every aide is identity-verified and background-screened before they can accept a visit, so you can welcome them into your home with confidence. And because CaraLoom coordinates the whole relationship in one place, your family circle stays updated on visits, notes, and any changes in your loved one\u2019s wellbeing.',
    ],
    included: {
      title: 'What a home health aide can help with',
      items: [
        'Bathing, grooming, dressing, and personal hygiene',
        'Mobility assistance and fall prevention',
        'Meal preparation and feeding support',
        'Medication reminders',
        'Light housekeeping, laundry, and errands',
        'Companionship and emotional support',
        'Transportation to appointments',
        'Respite relief for family caregivers',
      ],
    },
    whoFor: {
      title: 'Who benefits from a home health aide',
      items: [
        'Seniors who want to remain independent at home',
        'Adults recovering from illness, injury, or surgery',
        'People living with disabilities or mobility limits',
        'Family caregivers who need respite and relief',
        'Anyone who needs a hand with daily activities',
      ],
    },
    benefits: [
      { title: 'A caregiver who fits', desc: 'See real profiles \u2014 experience, languages, reviews \u2014 instead of accepting whoever happens to be on shift.' },
      { title: 'Consistency & trust', desc: 'Build a relationship with one dependable aide who learns your routine and preferences.' },
      { title: 'Background-checked', desc: 'Every caregiver is identity-verified and screened before their first visit.' },
      { title: 'One coordinated record', desc: 'Visits, notes, and family updates live in one HIPAA-ready dashboard \u2014 nothing lost between phone calls.' },
    ],
    faqs: [
      { q: 'What is the difference between a home health aide and a nurse?', a: 'A home health aide provides non-medical personal care \u2014 bathing, dressing, mobility, meals, and companionship. A nurse provides skilled clinical care like wound care and IV therapy. Many families use both, and CaraLoom can match you with either or both.' },
      { q: 'Can I choose my own caregiver?', a: 'Yes \u2014 the agency or independent practice running your case on CaraLoom surfaces real, verified profiles so you can request the aide whose experience and personality fit your loved one, rather than accepting whoever is on rotation.' },
      { q: 'Are caregivers background-checked?', a: 'Every caregiver completes identity verification and background screening before they can accept a visit, and their status is continuously monitored.' },
      { q: 'Can I schedule a few hours or full-time care?', a: 'Both. Arrange a few hours a week, daily visits, overnight support, or live-in care \u2014 whatever fits your loved one\u2019s needs and your budget.' },
      { q: 'Do you offer respite care for family caregivers?', a: 'Yes. Many families book an aide for regular respite so the primary family caregiver can rest, work, or travel without worry.' },
      { q: 'How are rates set?', a: 'The agency or independent caregiver behind your case sets their hourly rate up front in their CaraLoom profile, so you see the actual cost of care before booking \u2014 no surprise line items.' },
    ],
    related: ['companion-care', 'private-duty-nursing', 'dementia-alzheimers-care'],
  },

  'hospice-support': {
    slug: 'hospice-support',
    name: 'Hospice & Palliative Support',
    icon: Heart,
    eyebrow: 'Comfort, dignity, presence',
    h1: 'Hospice & Palliative Support at Home',
    metaTitle: 'Hospice & Palliative Care Support at Home | CaraLoom',
    metaDescription:
      'Compassionate in-home hospice and palliative support nationwide \u2014 comfort-focused nurses and aides for pain management, symptom relief, and family support.',
    keywords: [
      'hospice care at home', 'palliative care at home', 'end of life care',
      'in-home hospice support', 'comfort care',
    ],
    heroImage: 'https://images.pexels.com/photos/7551660/pexels-photo-7551660.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=900',
    heroAlt: 'Compassionate caregiver holding a patient\u2019s hand',
    intro: [
      'When the goal shifts from cure to comfort, families deserve clinicians who lead with both skill and tenderness. CaraLoom connects families across the United States with experienced hospice and palliative-care professionals who provide comfort-focused support at home \u2014 managing pain and symptoms while honoring the dignity and wishes of the person you love.',
      'Our verified nurses and aides work alongside your hospice team or physician to keep your loved one comfortable, calm, and surrounded by family. From symptom relief and gentle personal care to simply being a steady, reassuring presence, they help make difficult days softer for everyone in the home.',
      'Hospice is as much about the family as the patient. CaraLoom clinicians offer guidance, respite, and emotional support to caregivers \u2014 so you can spend precious time being a daughter, son, or spouse, rather than a full-time nurse. Every visit is coordinated in one place, with notes shared across your family circle.',
    ],
    included: {
      title: 'What hospice & palliative support includes',
      items: [
        'Pain and symptom management',
        'Comfort-focused personal care',
        'Medication administration and comfort kits',
        'Emotional and spiritual support',
        'Family caregiver guidance and respite',
        'Coordination with your hospice team or physician',
        'Overnight and around-the-clock presence',
        'Bereavement and end-of-life support',
      ],
    },
    whoFor: {
      title: 'Who hospice support is for',
      items: [
        'Patients with a life-limiting or terminal illness',
        'Families who want their loved one to remain at home',
        'Those seeking comfort-focused, dignity-first care',
        'Caregivers who need respite and emotional support',
        'Anyone navigating advanced or end-stage illness',
      ],
    },
    benefits: [
      { title: 'Comfort first', desc: 'Experienced clinicians focused on pain relief, dignity, and quality of life.' },
      { title: 'Support for the whole family', desc: 'Respite, guidance, and emotional support so families can simply be present.' },
      { title: 'Coordinated with your team', desc: 'We work alongside your hospice provider and physician for seamless care.' },
      { title: 'Presence when it matters', desc: 'Arrange overnight or around-the-clock support during the most tender hours.' },
    ],
    faqs: [
      { q: 'What is the difference between hospice and palliative care?', a: 'Palliative care focuses on comfort and symptom relief at any stage of a serious illness and can be provided alongside curative treatment. Hospice is comfort-focused care for those near the end of life. CaraLoom can match you with clinicians experienced in both.' },
      { q: 'Does CaraLoom replace my hospice agency?', a: 'No \u2014 our clinicians complement and coordinate with your existing hospice team or physician, adding extra hours of comfort-focused presence and personal care at home.' },
      { q: 'Can you provide overnight or 24-hour hospice support?', a: 'Yes. Many families arrange overnight or continuous coverage during the final weeks so a loved one is never alone and family caregivers can rest.' },
      { q: 'Do clinicians help with emotional and spiritual support?', a: 'Our hospice-experienced clinicians provide compassionate emotional support for both patients and families, and can coordinate spiritual or bereavement resources.' },
      { q: 'How are hospice clinicians vetted?', a: 'Every clinician is license-verified and background-checked, and you can review their hospice and palliative experience before booking.' },
      { q: 'How quickly can support begin?', a: 'We understand timing is sensitive. Many families are matched and supported within a day, and we prioritize urgent end-of-life needs.' },
    ],
    related: ['private-duty-nursing', 'home-health-aide', 'companion-care'],
  },

  'companion-care': {
    slug: 'companion-care',
    name: 'Companion Care',
    icon: Users,
    eyebrow: 'Connection & wellbeing',
    h1: 'Companion Care for Seniors',
    metaTitle: 'Companion Care for Seniors | CaraLoom',
    metaDescription:
      'Friendly, vetted companions for seniors nationwide \u2014 conversation, activities, errands, and a watchful eye against isolation. Transparent rates.',
    keywords: [
      'companion care', 'senior companion', 'companionship for elderly',
      'companion care', 'elderly companion services',
    ],
    heroImage: 'https://images.pexels.com/photos/18459198/pexels-photo-18459198.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=900',
    heroAlt: 'Companion sharing tea with an elderly woman',
    intro: [
      'Loneliness is one of the most overlooked health risks for older adults \u2014 linked to faster cognitive decline, depression, and even shorter lifespans. Companion care fights isolation with genuine human connection: a friendly, reliable person who shows up to share conversation, activities, and the small moments that make a day brighter.',
      'CaraLoom connects families across the United States with warm, vetted companions who provide non-medical support and companionship at home. From playing cards and reminiscing over old photos to walks, errands, and rides to appointments, a good companion keeps your loved one engaged, social, and safe.',
      'Companions also serve as a watchful set of eyes. They notice changes \u2014 a skipped meal, a new ache, a shift in mood \u2014 and keep your family circle informed, so small concerns are caught early. You choose the companion yourself from verified profiles, building a relationship that feels less like a service and more like family.',
    ],
    included: {
      title: 'What companion care includes',
      items: [
        'Conversation, games, and shared hobbies',
        'Walks, light exercise, and outings',
        'Meal prep and mealtime company',
        'Errands, shopping, and appointment rides',
        'Help with phone calls, mail, and technology',
        'Medication and appointment reminders',
        'Wellbeing check-ins and family updates',
        'A friendly, watchful presence against isolation',
      ],
    },
    whoFor: {
      title: 'Who companion care is for',
      items: [
        'Seniors living alone who feel isolated',
        'Older adults who need a little supervision and company',
        'Families living far away who want a trusted local presence',
        'People in early-stage memory decline',
        'Anyone who would simply enjoy reliable companionship',
      ],
    },
    benefits: [
      { title: 'Real connection', desc: 'A consistent, friendly face who genuinely brightens your loved one\u2019s day.' },
      { title: 'Early warning', desc: 'Companions notice subtle changes and keep your family informed.' },
      { title: 'Coordinated in one place', desc: 'Visits, notes, and family updates live on a single HIPAA-ready dashboard.' },
      { title: 'Flexible & affordable', desc: 'Book a few hours or daily visits at rates set up-front by the companion or their agency.' },
    ],
    faqs: [
      { q: 'What does a companion caregiver do?', a: 'Companions provide non-medical support and friendship \u2014 conversation, activities, meals, errands, rides, reminders, and a watchful eye. They keep seniors engaged and safe without providing clinical care.' },
      { q: 'How is companion care different from a home health aide?', a: 'Companion care focuses on social connection and light support, while a home health aide also handles hands-on personal care like bathing and mobility. Many families combine both.' },
      { q: 'Can a companion drive my parent to appointments?', a: 'Yes. Many companions provide transportation and accompany your loved one to appointments, errands, and outings.' },
      { q: 'Is companion care helpful for early dementia?', a: 'Very. A familiar, patient companion provides routine, engagement, and gentle supervision that benefits people in early-stage memory decline.' },
      { q: 'How do I choose the right companion?', a: 'Browse verified profiles with interests, languages, and reviews, then message a few candidates and pick the personality that fits your loved one best.' },
      { q: 'How much does companion care cost?', a: 'Companions and their agencies publish their hourly rate up front in their CaraLoom profile, so you see the real cost of care before booking \u2014 no surprise line items at the end of the month.' },
    ],
    related: ['home-health-aide', 'dementia-alzheimers-care', 'private-duty-nursing'],
  },

  'dementia-alzheimers-care': {
    slug: 'dementia-alzheimers-care',
    name: 'Dementia & Alzheimer\u2019s Care',
    icon: Brain,
    eyebrow: 'Specialized memory care',
    h1: 'Dementia & Alzheimer\u2019s Care at Home',
    metaTitle: 'Dementia & Alzheimer\u2019s Home Care | CaraLoom',
    metaDescription:
      'Specialized in-home dementia and Alzheimer\u2019s care nationwide \u2014 trained caregivers for safety, routine, and dignity. Family support included.',
    keywords: [
      'dementia care at home', 'Alzheimer\u2019s home care', 'memory care at home',
      'dementia caregiver', 'in-home Alzheimer\u2019s care',
    ],
    heroImage: 'https://images.pexels.com/photos/7156886/pexels-photo-7156886.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=900',
    heroAlt: 'Caregiver gently supporting a senior with memory loss',
    intro: [
      'Caring for someone with dementia or Alzheimer\u2019s is one of the hardest journeys a family can face. Familiar surroundings, gentle routines, and a patient, trained caregiver can make an enormous difference \u2014 reducing agitation, preserving dignity, and helping your loved one stay safely at home for as long as possible.',
      'CaraLoom connects families across the United States with caregivers experienced in memory care. They understand how to redirect with compassion, structure a calming daily routine, prevent wandering and falls, and communicate in ways that reduce fear and confusion. You choose the right caregiver yourself from verified, reviewed profiles.',
      'Memory care is also about supporting the family. Our caregivers provide respite so exhausted relatives can rest, share practical guidance on managing difficult behaviors, and keep your family circle updated on how each day unfolds. The result is safer days for your loved one and real relief for you.',
    ],
    included: {
      title: 'What dementia & Alzheimer\u2019s care includes',
      items: [
        'Calming daily routines and structure',
        'Safety monitoring and wandering prevention',
        'Compassionate redirection and behavior support',
        'Personal care with patience and dignity',
        'Cognitive engagement and memory activities',
        'Medication reminders and appointment support',
        'Family education and caregiver respite',
        'Ongoing updates to your family circle',
      ],
    },
    whoFor: {
      title: 'Who memory care is for',
      items: [
        'Adults living with Alzheimer\u2019s or other dementias',
        'Families wanting their loved one to stay home safely',
        'People experiencing confusion, agitation, or wandering',
        'Caregivers approaching burnout who need respite',
        'Anyone navigating progressive memory loss',
      ],
    },
    benefits: [
      { title: 'Trained in memory care', desc: 'Caregivers skilled in redirection, routine, and reducing agitation.' },
      { title: 'Safety at home', desc: 'Wandering prevention, fall reduction, and a calm, structured environment.' },
      { title: 'Relief for families', desc: 'Respite and guidance so you can rest and recharge with confidence.' },
      { title: 'Dignity always', desc: 'Patient, compassionate care that honors who your loved one is.' },
    ],
    faqs: [
      { q: 'Are caregivers trained specifically for dementia?', a: 'Yes. You can filter for caregivers with memory-care experience and review how they describe their approach to redirection, routine, and behavior support before booking.' },
      { q: 'Can in-home care prevent the need for a memory-care facility?', a: 'For many families, yes \u2014 especially in early and middle stages. Consistent, trained in-home care with safety measures can let a loved one remain at home longer and more safely.' },
      { q: 'How do caregivers handle agitation and wandering?', a: 'Experienced memory-care caregivers use calm redirection, structured routines, and environmental cues to reduce agitation, and they take proactive steps to prevent wandering and falls.' },
      { q: 'Do you support the family too?', a: 'Absolutely. Caregivers provide respite, share practical guidance for difficult behaviors, and keep your family circle informed about each day.' },
      { q: 'Can care scale as the disease progresses?', a: 'Yes. You can increase hours, add overnight coverage, or bring in skilled nursing as needs change \u2014 all coordinated in one place.' },
      { q: 'How are memory-care caregivers vetted?', a: 'Every caregiver is identity-verified and background-checked, with continuous monitoring, so you can welcome them into your home with confidence.' },
    ],
    related: ['companion-care', 'home-health-aide', 'private-duty-nursing'],
  },

  'post-surgery-care': {
    slug: 'post-surgery-care',
    name: 'Post-Surgical & Hospital-to-Home Care',
    icon: Activity,
    eyebrow: 'Recover safely at home',
    h1: 'Post-Surgical & Hospital-to-Home Care',
    metaTitle: 'Post-Surgery & Hospital-to-Home Care | CaraLoom',
    metaDescription:
      'Recover safely at home after surgery or a hospital stay with vetted nurses and aides nationwide \u2014 wound care, medication, mobility, and monitoring.',
    keywords: [
      'post surgery care at home', 'hospital to home care', 'post-operative care',
      'recovery care at home', 'transitional care',
    ],
    heroImage: 'https://images.pexels.com/photos/7659570/pexels-photo-7659570.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=900',
    heroAlt: 'Nurse helping a patient recover at home after surgery',
    intro: [
      'The days right after surgery or a hospital discharge are when recovery can go wonderfully \u2014 or go sideways. Missed medications, an unnoticed wound infection, or a fall can send a patient right back to the hospital. The right in-home support during this window protects your recovery and your peace of mind.',
      'CaraLoom connects families across the United States with vetted nurses and caregivers who specialize in transitional, post-surgical care. They manage wound care and medications, watch for warning signs, assist with safe mobility, and follow your discharge instructions to the letter \u2014 so you heal at home instead of in a hospital bed.',
      'Because care is coordinated in one place with clear, AI-assisted notes, your surgeon and family circle stay informed about your progress. The agency or independent practice running your case publishes their rates up front in their CaraLoom profile, and you arrange exactly the hours you need \u2014 from a few check-in visits to round-the-clock recovery support.',
    ],
    included: {
      title: 'What post-surgical care includes',
      items: [
        'Wound care and dressing changes',
        'Medication management and pain control',
        'Vital signs and infection monitoring',
        'Safe mobility, transfers, and fall prevention',
        'Help following discharge instructions',
        'Personal care during recovery',
        'Coordination with your surgeon and physician',
        'Clear notes shared with your family circle',
      ],
    },
    whoFor: {
      title: 'Who post-surgical care is for',
      items: [
        'Patients recently discharged after surgery',
        'Adults recovering from a hospital or rehab stay',
        'People who live alone and need recovery support',
        'Families wanting to prevent readmission',
        'Anyone needing a safe transition home',
      ],
    },
    benefits: [
      { title: 'Avoid readmission', desc: 'Close monitoring and fast escalation keep small problems from becoming emergencies.' },
      { title: 'Follow the plan', desc: 'Clinicians follow your discharge instructions precisely for a smoother recovery.' },
      { title: 'Heal at home', desc: 'Recover where you\u2019re most comfortable, with professional support on hand.' },
      { title: 'Flexible hours', desc: 'From daily check-ins to 24-hour coverage \u2014 arrange exactly what you need.' },
    ],
    faqs: [
      { q: 'How soon after surgery can care start?', a: 'Ideally the day you come home. CaraLoom can match you with a vetted clinician before discharge so support is in place the moment you arrive.' },
      { q: 'Can post-surgical care reduce hospital readmissions?', a: 'Yes. Proper wound care, medication management, mobility support, and early detection of complications are among the most effective ways to prevent avoidable readmissions.' },
      { q: 'Do you provide wound care after surgery?', a: 'Yes. Many CaraLoom nurses specialize in post-operative wound care and dressing changes, and follow your surgeon\u2019s instructions closely.' },
      { q: 'What if I live alone?', a: 'Post-surgical care is especially valuable for people recovering alone \u2014 you can arrange the hours and overnight coverage that keep you safe while you heal.' },
      { q: 'Will my surgeon stay informed?', a: 'Yes. Each visit produces clear notes, and clinicians coordinate with your surgeon and physician so everyone tracks your recovery.' },
      { q: 'How are recovery clinicians vetted?', a: 'All clinicians are identity-verified, license-checked, and background-screened, with continuous monitoring, before they can accept visits.' },
    ],
    related: ['skilled-nursing', 'private-duty-nursing', 'home-health-aide'],
  },
};

// De-emphasize low-acuity lines (companion/wellness) — lead with medically
// necessary, high-complexity care. Companion care stays reachable (SEO) but last.
export const SERVICE_LIST = [
  SERVICES['post-surgery-care'],
  SERVICES['skilled-nursing'],
  SERVICES['private-duty-nursing'],
  SERVICES['hospice-support'],
  SERVICES['dementia-alzheimers-care'],
  SERVICES['home-health-aide'],
  SERVICES['companion-care'],
];
